import {
  createContext,
  use,
  useCallback,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

/**
 * Which review items the Checkpoint column lists, picked one at a time from the review bar's picker. Every view but
 * Resolved lists open items only. `category` narrows Quality Review to one category label.
 */
export type ReviewView =
  | { kind: "open" }
  | { kind: "peer" }
  | { kind: "qa"; category?: string }
  | { kind: "waiting" }
  | { kind: "resolved" };

export const DEFAULT_REVIEW_VIEW: ReviewView = { kind: "open" };

export const isDefaultReviewView = (view: ReviewView): boolean => view.kind === "open";

type ReviewSessionState = {
  active: { fieldId: string; itemId: string | null } | null;
  /** An item has been selected in this session. Stays true when a blur on another field clears `active`. */
  hasSelectedItem: boolean;
  composer:
    | { kind: "comment"; fieldId: string; fieldTitle: string }
    | { kind: "reply"; parentId: string; replyToId: string }
    | null;
  editingCommentId: string | null;
  view: ReviewView;
  expandedReplies: string[];
  expandRequestId: string | null;
  navigationOrder: string[];
  /** The panel has measured its cards and published their order at least once. */
  isNavigationOrderPublished: boolean;
  suggestEditFieldId: string | null;
  conflictCommentId: string | null;
  /** Whether settling the conflict moves on to the next card (false from the author's to-fix list). */
  conflictAdvance: boolean;
  fieldRemountKeys: Record<string, number>;
  applyingFieldId: string | null;
};

export type ReviewComposer = NonNullable<ReviewSessionState["composer"]>;

/** A new comment's draft is keyed by its field id, a reply's by the thread it answers. */
const commentDraftKey = (composer: ReviewComposer): string =>
  composer.kind === "comment" ? composer.fieldId : `reply:${composer.parentId}`;

type ReviewSessionAction =
  | { type: "activate"; fieldId: string; itemId: string | null }
  | { type: "deactivate"; fieldId: string }
  | { type: "openComposer"; composer: ReviewComposer }
  | { type: "closeComposer" }
  /** Closes `composer` if it is still the open one, keeping its draft for when it reopens there. */
  | { type: "setAsideComposer"; composer: ReviewComposer }
  | { type: "startEditing"; commentId: string }
  | { type: "stopEditing" }
  | { type: "setReviewView"; view: ReviewView }
  | { type: "toggleReplies"; rootId: string }
  | { type: "expandReplies"; rootId: string }
  | { type: "requestExpand"; itemId: string | null }
  | { type: "setNavigationOrder"; itemIds: string[] }
  | { type: "setSuggestEditField"; fieldId: string | null }
  | { type: "closeSuggestEdit"; fieldId: string }
  | { type: "setApplyingField"; fieldId: string | null }
  | { type: "setConflictComment"; commentId: string | null; advance?: boolean }
  | { type: "remountField"; fieldId: string }
  | { type: "reset" };

/** Unsent composer text by `commentDraftKey`, outside session state so typing doesn't re-render every field badge. */
type CommentDraftStore = {
  get: (key: string) => string;
  set: (key: string, text: string) => void;
  subscribe: (listener: () => void) => () => void;
};

const createCommentDraftStore = (): CommentDraftStore & { clear: () => void } => {
  const drafts = new Map<string, string>();
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach((listener) => listener());
  return {
    get: (key) => drafts.get(key) ?? "",
    set: (key, text) => {
      if (text) drafts.set(key, text);
      else drafts.delete(key);
      notify();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    clear: () => {
      drafts.clear();
      notify();
    },
  };
};

type ReviewSessionValue = {
  state: ReviewSessionState;
  dispatch: (action: ReviewSessionAction) => void;
  drafts: CommentDraftStore;
};

const initialState: ReviewSessionState = {
  active: null,
  hasSelectedItem: false,
  composer: null,
  editingCommentId: null,
  view: DEFAULT_REVIEW_VIEW,
  expandedReplies: [],
  expandRequestId: null,
  navigationOrder: [],
  isNavigationOrderPublished: false,
  suggestEditFieldId: null,
  conflictCommentId: null,
  conflictAdvance: true,
  fieldRemountKeys: {},
  applyingFieldId: null,
};

const stopEditing = (state: ReviewSessionState): ReviewSessionState =>
  state.editingCommentId ? { ...state, editingCommentId: null } : state;

// A new comment posts as an open Team Review comment, so list those or it vanishes once sent.
const openComposer = (state: ReviewSessionState, composer: ReviewComposer): ReviewSessionState => {
  if (composer.kind !== "comment" || state.view.kind === "open" || state.view.kind === "peer") {
    return { ...state, composer };
  }
  return { ...state, composer, view: DEFAULT_REVIEW_VIEW };
};

// Only the named box: another may have opened since this one was found off the page.
const setAsideComposer = (state: ReviewSessionState, composer: ReviewComposer): ReviewSessionState =>
  state.composer && commentDraftKey(state.composer) === commentDraftKey(composer)
    ? { ...state, composer: null }
    : state;

// The same order published again keeps the state, so the bar doesn't re-render for nothing.
const setNavigationOrder = (state: ReviewSessionState, itemIds: string[]): ReviewSessionState => {
  const isSameOrder =
    state.isNavigationOrderPublished &&
    state.navigationOrder.length === itemIds.length &&
    state.navigationOrder.every((id, index) => id === itemIds[index]);
  return isSameOrder ? state : { ...state, navigationOrder: itemIds, isNavigationOrderPublished: true };
};

const reviewSessionReducer = (state: ReviewSessionState, action: ReviewSessionAction): ReviewSessionState => {
  switch (action.type) {
    case "activate":
      if (state.active?.fieldId === action.fieldId && state.active.itemId === action.itemId) return state;
      return {
        ...state,
        active: { fieldId: action.fieldId, itemId: action.itemId },
        hasSelectedItem: state.hasSelectedItem || action.itemId !== null,
      };
    case "deactivate":
      if (!state.active || state.active.fieldId === action.fieldId) return state;
      return { ...state, active: null };
    case "openComposer":
      return openComposer(state, action.composer);
    case "closeComposer":
      // The dispatch wrapper in useCreateReviewSession drops the closed composer's draft.
      return state.composer ? { ...state, composer: null } : state;
    case "setAsideComposer":
      return setAsideComposer(state, action.composer);
    case "startEditing":
      return { ...state, editingCommentId: action.commentId };
    case "stopEditing":
      return stopEditing(state);
    case "setReviewView":
      return { ...state, view: action.view };
    case "toggleReplies":
      return {
        ...state,
        expandedReplies: state.expandedReplies.includes(action.rootId)
          ? state.expandedReplies.filter((id) => id !== action.rootId)
          : [...state.expandedReplies, action.rootId],
      };
    case "expandReplies":
      if (state.expandedReplies.includes(action.rootId)) return state;
      return { ...state, expandedReplies: [...state.expandedReplies, action.rootId] };
    case "requestExpand":
      return { ...state, expandRequestId: action.itemId };
    case "setNavigationOrder":
      return setNavigationOrder(state, action.itemIds);
    case "setSuggestEditField":
      return { ...state, suggestEditFieldId: action.fieldId };
    case "closeSuggestEdit":
      return state.suggestEditFieldId === action.fieldId ? { ...state, suggestEditFieldId: null } : state;
    case "setApplyingField":
      return { ...state, applyingFieldId: action.fieldId };
    case "setConflictComment":
      return { ...state, conflictCommentId: action.commentId, conflictAdvance: action.advance ?? true };
    case "remountField":
      return {
        ...state,
        fieldRemountKeys: {
          ...state.fieldRemountKeys,
          [action.fieldId]: (state.fieldRemountKeys[action.fieldId] ?? 0) + 1,
        },
      };
    case "reset":
      return initialState;
  }
};

export const ReviewSessionContext = createContext<ReviewSessionValue | null>(null);

export const useCreateReviewSession = (reportId?: string): ReviewSessionValue => {
  const [state, dispatchAction] = useReducer(reviewSessionReducer, initialState);
  const [drafts] = useState(createCommentDraftStore);
  const openDraftKeyRef = useRef<string | null>(null);

  // The report route doesn't remount when only the report id changes, so start a fresh session here.
  const [sessionReportId, setSessionReportId] = useState(reportId);
  if (sessionReportId !== reportId) {
    setSessionReportId(reportId);
    dispatchAction({ type: "reset" });
  }
  useLayoutEffect(() => {
    drafts.clear();
    openDraftKeyRef.current = null;
  }, [drafts, reportId]);

  const dispatch = useCallback(
    (action: ReviewSessionAction) => {
      if (action.type === "openComposer") {
        openDraftKeyRef.current = commentDraftKey(action.composer);
      } else if (action.type === "closeComposer" && openDraftKeyRef.current) {
        drafts.set(openDraftKeyRef.current, "");
        openDraftKeyRef.current = null;
      } else if (action.type === "setAsideComposer" && openDraftKeyRef.current === commentDraftKey(action.composer)) {
        openDraftKeyRef.current = null;
      }
      dispatchAction(action);
    },
    [drafts],
  );

  return useMemo(() => ({ state, dispatch, drafts }), [state, dispatch, drafts]);
};

const noDrafts: CommentDraftStore = { get: () => "", set: () => {}, subscribe: () => () => {} };

const noSession: ReviewSessionValue = { state: initialState, dispatch: () => {}, drafts: noDrafts };

export const useReviewSession = (): ReviewSessionValue => use(ReviewSessionContext) ?? noSession;

/** A composer's draft, kept until it is sent or closed so it survives the composer unmounting. */
export const useCommentDraft = (target: ReviewComposer): [string, (text: string) => void] => {
  const { drafts } = useReviewSession();
  const key = commentDraftKey(target);
  const draft = useSyncExternalStore(drafts.subscribe, () => drafts.get(key));
  return [draft, (text: string) => drafts.set(key, text)];
};
